import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase, getStoredUser } from "../supabase";
import { clearLocalUserData } from "../utils/clearLocalUserData";
import { disablePush } from "../utils/push";
import { IoAdd, IoSearch, IoLogInOutline } from "react-icons/io5";
import NotificationBell from "./NotificationBell";
import BrandLogo from "./BrandLogo";
import { alertDialog } from "../utils/dialog";
import { readProfileCache, mergeProfileCache } from "../utils/profileCache";

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(getStoredUser);
  // { username, avatar_url } — starts from the copy kept on this device so
  // the avatar in the nav is the real one from the first paint (see
  // profileCache.js), then gets replaced by the fresh fetch below.
  const [profile, setProfile] = useState(() => {
    const cached = readProfileCache(getStoredUser()?.id);
    return cached?.username ? { username: cached.username, avatar_url: cached.avatar_url || null } : null;
  });

  useEffect(() => {
    let mounted = true;
    // getSession() below AND onAuthStateChange's own immediate initial
    // firing both call loadProfile with the same user right at mount —
    // without this, a banned account could get signOut() + the suspension
    // alert + navigate("/") run twice back to back, showing the alert twice.
    let bannedHandled = false;
    // The current user, for checkBanned's interval/visibility triggers below
    // — those fire on a timer rather than from an auth event, so they can't
    // get the user passed to them the way loadProfile does.
    let currentUser = null;

    // A banned account gets signed out the moment this next runs, rather
    // than needing every page to check this individually. Banning a JWT
    // itself out from under an already-open tab isn't something this app
    // does — that account keeps working until this actually catches it, so
    // it's also run on a timer and on the tab coming back into view (see
    // below), not only when supabase-js happens to fire an auth event
    // (sign-in, sign-out, or the ~hourly token refresh).
    async function checkBanned() {
      if (!currentUser) return false;
      const { data: banned, error } = await supabase.rpc("am_i_banned");
      if (error) {
        console.log("BAN CHECK ERROR:", error);
        return false;
      }
      if (!banned || bannedHandled) return !!banned;
      bannedHandled = true;

      await supabase.auth.signOut();
      clearLocalUserData();
      disablePush();
      if (mounted) {
        setUser(null);
        setProfile(null);
        await alertDialog({
          title: "Account suspended",
          message: "Your account has been suspended for violating our community guidelines."
        });
        navigate("/");
      }
      return true;
    }

    async function loadProfile(u) {
      // Reset the dedup flag whenever the signed-in account actually
      // changes (sign-out then sign-in as someone else, in the same tab) —
      // otherwise a banned account handled once here would permanently
      // suppress the check for every account that signs in afterward. It
      // stays true across the concurrent double-call at mount for the SAME
      // account (see the comment above bannedHandled), since u.id doesn't
      // change between those two calls.
      if (u?.id !== currentUser?.id) bannedHandled = false;
      currentUser = u;
      if (!u) {
        if (mounted) setProfile(null);
        return;
      }

      // `banned` isn't grantable to `authenticated` for SELECT (same reason
      // `gender` needs get_my_gender() — see Profile.jsx) — a .select() that
      // names it fails the WHOLE request with 42501, even for your own row,
      // so it's fetched separately through checkBanned's own RPC instead.
      const [{ data, error }, banned] = await Promise.all([
        supabase.from("users").select("username, avatar_url").eq("id", u.id).maybeSingle(),
        checkBanned()
      ]);

      if (error) {
        console.log("LOAD PROFILE ERROR:", error);
      }
      if (banned) return;

      if (mounted) setProfile(data || null);
      // This fetch can finish after a logout that already wiped the cache —
      // only write it back if that account is still the one signed in.
      if (data?.username && getStoredUser()?.id === u.id) {
        mergeProfileCache(u.id, { username: data.username, avatar_url: data.avatar_url || "" });
      }
    }

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const u = data.session?.user ?? null;
      setUser(u);
      loadProfile(u);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user || null;
      setUser(u);
      loadProfile(u);
    });

    const banCheckInterval = setInterval(checkBanned, 3 * 60 * 1000);
    function onVisible() {
      if (document.visibilityState === "visible") checkBanned();
    }
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe();
      clearInterval(banCheckInterval);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function isActive(path) {
    return location.pathname === path;
  }

  // Auto-hide both bars while scrolling down (more room for content on a
  // small screen), and bring them back the moment the user scrolls up even
  // slightly — the usual mobile-feed pattern. Below `HIDE_THRESHOLD` they
  // always stay put, so a small bounce/overscroll right at the top of a
  // page can't flicker them away.
  const [navHidden, setNavHidden] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const HIDE_THRESHOLD = 80;
    let ticking = false;

    function handleScroll() {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const goingDown = currentY > lastScrollY.current;

        setNavHidden(goingDown && currentY > HIDE_THRESHOLD);

        lastScrollY.current = currentY;
        ticking = false;
      });
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* ===== Slim top brand strip (Home + Trending + Saved + Profile) ===== */}
      {/* transform must be the literal string "none" (not "translateY(0)")
          when shown — Profile.jsx portals its settings dropdown into the
          slot below, which is position:fixed on mobile, and ANY non-"none"
          transform on an ancestor (even a visually-static translateY(0))
          creates a new containing block for it, breaking its fixed
          positioning relative to this bar's tiny box instead of the
          viewport. */}
      {(isActive("/") || isActive("/trending") || isActive("/saved") || isActive("/profile")) && (
        <div style={{ ...topBar, transform: navHidden ? "translateY(-100%)" : "none" }}>
          <Link to="/" style={logo}>
            <BrandLogo />
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <NotificationBell user={user} />
            {isActive("/profile") ? (
              <div id="navbar-profile-settings-slot" style={searchIconBtn} />
            ) : (
              <Link to="/search" style={searchIconBtn} aria-label="Search">
                <IoSearch />
              </Link>
            )}
          </div>
        </div>
      )}

      {/* ===== Floating pill navigation (icon-only, like Instagram/Snapchat) =====
          Portaled — verified live that nothing currently sits between here
          and document.body with a transform/filter (which would hijack this
          nav's position:fixed to be relative to that ancestor's box instead
          of the viewport, same class of bug fixed in Toast.jsx and
          ShareButton.jsx), but that's an assumption about the rest of the
          app's future markup, not a guarantee. topBar above is NOT portaled
          along with it — it's position:sticky, which needs to stay in this
          exact spot in the document flow to stick correctly; portaling it
          would move it out of the page's layout entirely and break that. */}
      {createPortal(
        <nav
          className="virsa-pill-nav"
          style={{
            ...pillNav,
            transform: navHidden
              ? "translateX(-50%) translateY(150%)"
              : "translateX(-50%) translateY(0)"
          }}
        >
          <style>{`
            .virsa-pill-nav .nav-item {
              display: flex;
              align-items: center;
              justify-content: center;
              text-decoration: none;
              color: var(--text-primary);
              font-weight: 700;
              background: none;
              border: none;
              border-radius: 999px;
              cursor: pointer;
              padding: 2px 12px;
              transition: background-color .15s;
            }
            /* Nothing styled the "active" class the JSX already computed
               below — every icon looked identical whether it was the
               current tab or not, with zero visual feedback about where
               you are (Profile was the one exception, via its own inline
               avatar-border style). A background pill (rather than a
               filter/tint on the icon itself) works regardless of what
               colors any given icon's PNG actually uses. */
            .virsa-pill-nav .nav-item.active {
              background: color-mix(in srgb, var(--accent-primary) 18%, transparent);
            }
            .virsa-pill-nav .nav-icon { font-size: 38px; line-height: 1; display: flex; }
            .virsa-pill-nav .nav-icon-img {
              width: 44px;
              height: 44px;
              display: block;
            }

            /* The bar ends right at the first and last icon — the spacing
               between icons comes from their own side padding, so the end
               icons only keep a small inset (enough that their corners
               do not poke out past the pill's rounded ends). */
            .virsa-pill-nav > a:first-of-type { padding-left: 8px !important; }
            .virsa-pill-nav > a:last-of-type { padding-right: 8px !important; }

            @media (max-width: 380px) {
              .virsa-pill-nav .nav-item { padding: 2px 8px; }
            }
          `}</style>

          <Link to="/" className={`nav-item ${isActive("/") ? "active" : ""}`} aria-label="Home" aria-current={isActive("/") ? "page" : undefined}>
            <img src="/nav-icons/home.png" alt="" className="nav-icon-img" />
          </Link>

          <Link to="/trending" className={`nav-item ${isActive("/trending") ? "active" : ""}`} aria-label="Trending" aria-current={isActive("/trending") ? "page" : undefined}>
            <img src="/nav-icons/trending.png" alt="" className="nav-icon-img" />
          </Link>

          <Link to="/upload" style={fabWrap} aria-label="Upload a design">
            <span style={fab}><IoAdd /></span>
          </Link>

          <Link to="/saved" className={`nav-item ${isActive("/saved") ? "active" : ""}`} aria-label="Saved" aria-current={isActive("/saved") ? "page" : undefined}>
            <img src="/nav-icons/saved.png" alt="" className="nav-icon-img" />
          </Link>

          {user ? (
            <Link
              to="/profile"
              className={`nav-item ${isActive("/profile") ? "active" : ""}`}
              aria-label="Profile"
              aria-current={isActive("/profile") ? "page" : undefined}
              style={{ padding: "2px 12px" }}
            >
              <img
                src={profile?.avatar_url || "/default-avatar.svg"}
                alt="Profile"
                style={{
                  ...navAvatar,
                  border: `2px solid ${isActive("/profile") ? "var(--text-primary)" : "var(--border-input)"}`,
                }}
              />
            </Link>
          ) : (
            <Link to="/login" className={`nav-item ${isActive("/login") ? "active" : ""}`} aria-label="Login" aria-current={isActive("/login") ? "page" : undefined}>
              <span className="nav-icon"><IoLogInOutline /></span>
            </Link>
          )}
        </nav>,
        document.body
      )}
    </>
  );
}

export default Navbar;

/* ===== STYLES (Virsa palette) ===== */

const topBar = {
  position: "sticky",
  top: 0,
  zIndex: 900,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "3px 20px",
  background: "var(--bg-surface)",
  borderBottom: "1px solid var(--border-soft)",
  transition: "transform 0.25s ease",
};

const logo = {
  textDecoration: "none",
  // A plain inline link leaves a line-height gap under the logo inside the
  // bar; as a flex box the bar wraps the logo exactly.
  display: "flex",
};

const searchIconBtn = {
  display: "flex",
  alignItems: "center",
  fontSize: 28,
  textDecoration: "none",
  color: "var(--text-primary)",
  fontWeight: 700,
  lineHeight: 1,
};

const pillNav = {
  position: "fixed",
  left: "50%",
  transform: "translateX(-50%)",
  bottom: "calc(6px + env(safe-area-inset-bottom))",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "4px 0",
  borderRadius: 26,
  background: "var(--nav-pill-bg)",
  backdropFilter: "blur(36px) saturate(1.8)",
  WebkitBackdropFilter: "blur(36px) saturate(1.8)",
  // The outline is a half-pixel ring drawn as a shadow spread, not a border:
  // browsers never draw a border thinner than one device pixel, but a
  // spread is anti-aliased, so it really is thinner than a border on any screen.
  boxShadow: "0 8px 28px rgba(20,20,20,0.18), 0 0 0 0.5px var(--nav-pill-border)",
  border: "none",
  maxWidth: "94vw",
  transition: "transform 0.25s ease",
};

const fabWrap = {
  position: "relative",
  width: 48,
  height: 38,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
};

const fab = {
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: 40,
  height: 40,
  borderRadius: "50%",
  background: "var(--accent-primary)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 24,
  color: "var(--accent-contrast)",
  boxShadow: "0 6px 16px rgba(13,59,122,0.45)",
  border: "3px solid var(--bg-surface)",
};

const navAvatar = {
  width: 44,
  height: 44,
  borderRadius: "50%",
  objectFit: "cover",
  border: "2px solid var(--border-input)",
};
